// 「持ちもの」3 種（登録時に必要なもの／施設に提出するもの／子どもが使うもの）の要素。
// 1 つの配列に、リスト項目（既存の { name, url, note }）と PDF 書類を指すファイル項目が混在する。
//
// 保存される JSON のキーは snake_case（命名の理由は app/models/facility_detail/contents.rb）。
// 画面が扱う形は camelCase に揃えてあり、snake_case への変換は toFacilityDetailItemsPayload の 1 箇所だけ。
// そのため Admin プレビューはフォーム値をそのまま保護者側コンポーネントに渡せる。

export const LIST_ITEM_TYPE = 'item'
export const FILE_ITEM_TYPE = 'file'

export type FacilityDetailItemType =
  typeof LIST_ITEM_TYPE | typeof FILE_ITEM_TYPE

/**
 * 表示側（保護者ページ・Admin プレビュー）が受け取る形。
 *
 * 判別共用体にはしない。Admin のフォーム値は全キー固定のフラット型で（react-hook-form の Path<T> を
 * 壊さないため）、プレビューはそれをそのまま渡すので共用体だと代入できなくなる。判別は isFileItem で行う。
 */
export type FacilityDetailItem = {
  type?: FacilityDetailItemType
  name: string
  url?: string
  note?: string
  message?: string
  documentId?: number | null
  fileName?: string
  // Admin プレビュー専用。保存されないので保護者ページでは常に undefined
  previewUrl?: string
}

/**
 * Admin のフォーム値（react-hook-form が保持する形）。全キーを必ず持つ。
 *
 * previewUrl だけは画面の中だけで使う値で、サーバーには送らない
 * （toFacilityDetailItemsPayload が出力するキーを明示列挙しているため、自動的に落ちる）。
 * 保存前の配信 URL は 404 になる（参照されていない書類は公開しない仕様）ので、
 * アップロードした本人にだけ手元のファイルを見せるために持つ
 */
export type FacilityDetailItemFormValue = {
  type: FacilityDetailItemType
  name: string
  url: string
  note: string
  message: string
  documentId: number | null
  fileName: string
  previewUrl: string
}

/** サーバーから受け取った形。キーは snake_case で、既存データではファイル項目のキーが欠けている */
export type FacilityDetailItemSource = {
  type?: string
  name?: string
  url?: string
  note?: string
  message?: string
  document_id?: number | string | null
  file_name?: string
}

/** 送信する形。リスト項目・ファイル項目のどちらも全キーを持つ（理由は toFacilityDetailItemsPayload） */
export type FacilityDetailItemPayload = {
  type: FacilityDetailItemType
  name: string
  url: string
  note: string
  message: string
  document_id: string
  file_name: string
}

export const isFileItem = (item: FacilityDetailItem): boolean =>
  item.type === FILE_ITEM_TYPE

/**
 * 添付済みのファイル項目か。
 * 未添付（url が空）の行はサーバーの正規化で捨てられるため、画面にも出さない
 */
export const isRenderableFileItem = (
  item: FacilityDetailItem,
): item is FacilityDetailItem & { url: string } =>
  isFileItem(item) && (item.url || '').length > 0

/**
 * サーバーから受け取った配列をフォーム値に整える。
 * type が無い既存データはリスト項目とみなし、欠けているキーは空文字で埋める
 */
export const normalizeFacilityDetailItems = (
  items?: (FacilityDetailItemSource | null | undefined)[] | null,
): FacilityDetailItemFormValue[] => {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map((item) => {
    const source = item || {}

    return {
      type: source.type === FILE_ITEM_TYPE ? FILE_ITEM_TYPE : LIST_ITEM_TYPE,
      name: source.name || '',
      url: source.url || '',
      note: source.note || '',
      message: source.message || '',
      documentId: toDocumentId(source.document_id),
      fileName: source.file_name || '',
      previewUrl: '',
    }
  })
}

/**
 * フォーム値を送信する形に変換する。
 *
 * 出力するキーは明示列挙する。全行に同じキーを載せないと multipart の要素境界がずれるため
 * （条件と壊れ方は app/models/facility_detail/contents.rb の normalize_items）。
 * フォーム値にしか無い previewUrl が送られないのも、この明示列挙による。
 *
 * 保存しない行もここで落とす。リスト項目はテキスト未入力、ファイル項目は未添付のものが対象。
 * 入力が残っているファイル項目（× で添付だけ外した行）はここへ来る前に止める（unattachedFileItemOrdinals）
 */
export const toFacilityDetailItemsPayload = (
  items: FacilityDetailItemFormValue[],
): FacilityDetailItemPayload[] =>
  items
    .filter((item) =>
      isFileItem(item) ? item.url.length > 0 : item.name.length > 0,
    )
    .map((item) => ({
      type: item.type,
      name: item.name,
      url: item.url,
      note: item.note,
      message: item.message,
      document_id: item.documentId === null ? '' : String(item.documentId),
      file_name: item.fileName,
    }))

/**
 * type ごとの連番（「提出物 N」「ファイル N」）を作る。
 * 1 つの配列にリスト項目とファイル項目が混在するため、見出しの番号に index + 1 は使えない
 */
export const makeItemOrdinals = (
  items: { type?: FacilityDetailItemType }[],
): number[] => {
  const counts: Record<FacilityDetailItemType, number> = { item: 0, file: 0 }

  return items.map((item) => {
    const type = item.type === FILE_ITEM_TYPE ? FILE_ITEM_TYPE : LIST_ITEM_TYPE
    counts[type] += 1
    return counts[type]
  })
}

/**
 * 保存すると黙って消えてしまうファイル項目の見出し番号（「ファイル N」の N）。
 *
 * 未添付のファイル項目は toFacilityDetailItemsPayload で落ちる。テキストもメッセージも空の行は
 * リスト項目と同じ「書きかけを捨てる」だけだが、入力が残っている行は画面から消えたように見えない
 * （消えるのは保存＋リロードの後）。× で添付だけ外した直後がこれにあたる
 */
export const unattachedFileItemOrdinals = (
  items: FacilityDetailItemFormValue[],
): number[] => {
  const ordinals = makeItemOrdinals(items)

  return items
    .map((item, index) => ({ item, ordinal: ordinals[index] }))
    .filter(
      ({ item }) =>
        isFileItem(item) &&
        item.url.length === 0 &&
        (item.name.length > 0 || item.message.length > 0),
    )
    .map(({ ordinal }) => ordinal)
}

// document_id は JSON 経路では整数・multipart 経路では文字列で届く。数値でない値は未添付として扱う
const toDocumentId = (value?: number | string | null): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isInteger(parsed) ? parsed : null
}
